import { useEffect, useState } from 'react';
import type { EstimateMembershipOffer } from '@/types/estimate';
import type { Proposal, ProposalOption, ProposalSelectionPricing } from '@/types/proposal';
import {
  proposalOptionDefaultOfferKeys,
  proposalOptionSelectionChanges,
  proposalOptionWithSelection,
} from '@/lib/sales/proposalOptionBreakdown';

export interface ProposalSelectionPreview {
  option: ProposalOption;
  pricing: ProposalSelectionPricing;
  offer: EstimateMembershipOffer | null;
}

export interface ProposalPreviewSelection {
  option_index: number;
  lineItems?: Array<{ lineItemId: string; lineKey?: string; isSelected: boolean }>;
  acceptMembershipOffer: boolean;
  membershipBillingOptionId?: string;
}

export async function previewProposalSelection(
  id: string,
  selection: ProposalPreviewSelection,
  signal?: AbortSignal,
): Promise<ProposalSelectionPreview> {
  const response = await fetch(
    `${import.meta.env.VITE_OPERATIONS_URL ?? ''}/proposals/public/${id}/preview-option`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(selection),
      signal,
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(
      typeof body?.message === 'string'
        ? body.message
        : 'We could not confirm this price. Please try again.',
    );
  }
  const result: ProposalSelectionPreview = await response.json();
  if (
    !result.option ||
    !result.pricing ||
    !['subtotal', 'discount', 'membershipSavings', 'tax', 'total', 'depositDue'].every((key) =>
      Number.isFinite(result.pricing[key as keyof ProposalSelectionPricing]),
    )
  ) {
    throw new Error('We could not confirm this price. Please refresh and try again.');
  }
  return result;
}

/** A response belongs to one exact choice and quote version. Never allow a stale
 * response (or a failed request) to authorize a different scope or membership. */
export function useProposalSelectionPreview({
  proposal,
  enabled,
  optionalSelections,
  membershipSelections,
  membershipBillingSelections,
}: {
  proposal: Pick<Proposal, 'id' | 'options' | 'updatedAt' | 'selectionPreviewSupported'>;
  enabled: boolean;
  optionalSelections: Record<number, Set<string>>;
  membershipSelections: Record<number, boolean>;
  membershipBillingSelections: Record<number, string>;
}) {
  const [results, setResults] = useState<
    Record<number, { key: string; result?: ProposalSelectionPreview; error?: string }>
  >({});
  const [retry, setRetry] = useState(0);
  const serverPricing = enabled && proposal.selectionPreviewSupported === true;
  const requests = proposal.options.map((option, index) => {
    const selected = optionalSelections[index] ?? proposalOptionDefaultOfferKeys(option);
    const lineItems = proposalOptionSelectionChanges(option, selected);
    const member = membershipSelections[index] === true;
    const billing =
      membershipBillingSelections[index] ?? option.membershipPlanSnapshot?.offer?.billingOptionId;
    return serverPricing && (lineItems.length > 0 || member)
      ? {
          option_index: index,
          ...(lineItems.length ? { lineItems } : {}),
          acceptMembershipOffer: member,
          ...(member && billing ? { membershipBillingOptionId: billing } : {}),
        }
      : null;
  });
  // Include the payload, not just updatedAt: cached/local records need the same
  // protection when the parent replaces a quote without a version timestamp.
  const quoteKey = JSON.stringify([proposal.id, proposal.updatedAt, proposal.options]);
  const requestKey = JSON.stringify(requests);
  useEffect(() => {
    const selections: Array<ProposalPreviewSelection | null> = JSON.parse(requestKey);
    const controllers: AbortController[] = [];
    let live = true;
    selections.forEach((selection, index) => {
      if (!selection) return;
      const key = JSON.stringify([quoteKey, selection]);
      const controller = new AbortController();
      controllers.push(controller);
      setResults((current) => ({ ...current, [index]: { key } }));
      void previewProposalSelection(proposal.id, selection, controller.signal).then(
        (result) => {
          if (live) setResults((current) => ({ ...current, [index]: { key, result } }));
        },
        (error: unknown) => {
          if (live)
            setResults((current) => ({
              ...current,
              [index]: {
                key,
                error: error instanceof Error ? error.message : 'Unable to confirm price.',
              },
            }));
        },
      );
    });
    return () => {
      live = false;
      controllers.forEach((controller) => controller.abort());
    };
  }, [proposal.id, quoteKey, requestKey, retry]);

  const stateFor = (index: number) => {
    const request = requests[index];
    if (!request) return { ready: true, error: undefined, result: undefined };
    const key = JSON.stringify([quoteKey, request]);
    const state = results[index]?.key === key ? results[index] : undefined;
    return { ready: Boolean(state?.result), error: state?.error, result: state?.result };
  };
  const optionFor = (index: number): ProposalOption | undefined => {
    const raw = proposal.options[index];
    if (!raw || !enabled) return raw;
    const keys = optionalSelections[index] ?? proposalOptionDefaultOfferKeys(raw);
    if (!serverPricing) {
      return proposalOptionSelectionChanges(raw, keys).length
        ? proposalOptionWithSelection(raw, keys)
        : raw;
    }
    const result = stateFor(index).result;
    if (result)
      return {
        ...result.option,
        membershipPlanSnapshot: { ...result.option.membershipPlanSnapshot, offer: result.offer },
        selectionPricing: result.pricing,
      };
    // Display the changed checkboxes immediately, but retain the last quoted
    // money under an explicit updating/error message until it is confirmed.
    const changes = proposalOptionSelectionChanges(raw, keys);
    return {
      ...raw,
      lineItems: raw.lineItems.map((line) => {
        const change = changes.find((entry) =>
          entry.lineKey ? entry.lineKey === line.lineKey : entry.lineItemId === line.lineItemId,
        );
        return change ? { ...line, isSelected: change.isSelected } : line;
      }),
    };
  };
  return { optionFor, stateFor, retry: () => setRetry((current) => current + 1) };
}
